import { portalApi } from "./api";
import { freshPage } from "./session";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { portalLine } from "@shared/consumer-tvod";
import { useI18n } from "./i18n";
import {
  ArrowLeft,
  BookmarkIcon,
  DocumentIcon,
  HomeIcon,
  LogoutIcon,
  PlanIcon,
  SearchIcon,
  UserIcon,
} from "./icons";
import type { Route } from "./routes";
import { SEARCH_DEBOUNCE_MS, searchable, searchPath } from "./search";
import { usePortal } from "./state";
import type { Lang } from "./types";
import { Link } from "./ui";
import { useHasInformation } from "./information";

/**
 * The portal's frame: a sticky, branded header with the search built in,
 * and a footer carrying the provider's identity and its terms, privacy and
 * support links.
 *
 * ## A visitor gets no side menu and no search
 *
 * Signed out, there is nothing to navigate or buy (the per-title shop is
 * retired; organisations apply): the header carries the provider's mark,
 * "Ansök om konto" and "Logga in", and the language buttons. The side menu
 * and the search are for signed-in accounts.
 *
 * ## Search lives in the header
 *
 * One box on every page. On the store page it drives `/?q=` exactly as the
 * store's own box did (slice H): 250 ms after the last keystroke, at least
 * two letters or digits, the query REPLACES the address so back from a
 * title returns to the results. Typing on any other page goes to the store
 * with the query (a push, so back returns to where the search started).
 */

export function Header(props: {
  signedIn: boolean;
  route: Route;
  lang: Lang;
  onLang(lang: Lang): void;
  /** No search: a visitor, a viewer, or "Endast köpt innehåll". */
  hideSearch?: boolean;
}) {
  const { t } = useI18n();
  const { context } = usePortal();
  const hideSearch = props.hideSearch === true || !props.signedIn;
  const current = props.route.name;
  const aria = (names: Route["name"][]) =>
    names.includes(current) ? ("page" as const) : undefined;
  const logo = context.skin.theme?.logoUrl;
  const name = context.portal.name;
  return (
    <header className="header">
      <div className="header__bar wrap">
        {!props.signedIn ? (
          // No side menu for a visitor: the provider's mark lives here.
          <Link to="/" className="header__brand">
            {logo ? (
              <img className="header__logo" src={logo} alt={name} />
            ) : (
              <span className="header__name">{name}</span>
            )}
          </Link>
        ) : hideSearch ? (
          <span className="header__spacer" />
        ) : (
          <SearchBox route={props.route} />
        )}
        {!props.signedIn && (
          <div className="header__nav">
            <Link
              to="/login"
              className="header__link"
              aria-current={aria(["login"])}
            >
              {t("nav.login")}
            </Link>
            <Link
              to="/account/register"
              className="header__link header__cta"
              aria-current={aria(["register"])}
            >
              {t("nav.register")}
            </Link>
          </div>
        )}
        <div className="header__lang" role="group" aria-label={t("lang.label")}>
          {(["sv", "en"] as const).map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              aria-pressed={props.lang === l}
              onClick={() => props.onLang(l)}
            >
              {l.toUpperCase()}
              <span className="sr-only"> {t(`lang.${l}`)}</span>
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

/**
 * The side menu (PLAY-10, Sigge 2026-10-06: "keep the play menu on the
 * side"), for signed-in accounts only. The provider's mark on top, then Hem,
 * Sök, Er plan / Mitt innehåll and Konto. Icon plus a short label: a menu
 * people meet once a week must not be a row of riddles.
 */
export function SideNav(props: {
  /**
   * A purchaser gets "Er plan" instead of "Mitt innehåll" (it never plays);
   * a viewer only "Mitt innehåll" and its account (ORG-6) - no store, no
   * search.
   */
  kind?: "individual" | "purchaser" | "viewer";
  route: Route;
}) {
  const { context } = usePortal();
  const { t } = useI18n();
  const logo = context.skin.theme?.logoUrl;
  const name = context.portal.name;
  const current = props.route.name;
  const searching = current === "browse" && !!props.route.q;
  // Only when the provider has published something: no dead menu entry.
  const hasInformation = useHasInformation();
  const item = (
    to: string,
    label: string,
    icon: ReactNode,
    active: boolean
  ) => (
    <Link
      to={to}
      className="sidenav__item"
      aria-current={active ? "page" : undefined}
    >
      {icon}
      <span className="sidenav__label">{label}</span>
    </Link>
  );
  return (
    <nav className="sidenav" aria-label={t("nav.main")}>
      {logo ? (
        <Link to="/" className="sidenav__brand">
          <img src={logo} alt={name} />
        </Link>
      ) : (
        <Link to="/" className="sidenav__brand" aria-label={name}>
          <span aria-hidden="true">
            {name.trim().charAt(0).toUpperCase() || "·"}
          </span>
        </Link>
      )}
      {props.kind !== "viewer" &&
        item(
          "/",
          t("nav.home"),
          <HomeIcon className="sidenav__icon" />,
          ["browse", "genre", "title"].includes(current) && !searching
        )}
      {props.kind !== "viewer" && (
        <button
          type="button"
          className="sidenav__item"
          aria-current={searching ? "page" : undefined}
          onClick={() => {
            const box = document.querySelector<HTMLInputElement>(
              ".header-search__input"
            );
            box?.focus();
          }}
        >
          <SearchIcon className="sidenav__icon" />
          <span className="sidenav__label">{t("nav.search")}</span>
        </button>
      )}
      {props.kind === "purchaser"
        ? item(
            "/plan",
            t("nav.plan"),
            <PlanIcon className="sidenav__icon" />,
            current === "plan"
          )
        : item(
            props.kind === "viewer" ? "/" : "/my",
            t("nav.my"),
            <BookmarkIcon className="sidenav__icon" />,
            props.kind === "viewer" ? current !== "account" : current === "my"
          )}
      {hasInformation &&
        item(
          "/information",
          t("info.title"),
          <DocumentIcon className="sidenav__icon" />,
          current === "information" || current === "information-item"
        )}
      <span className="sidenav__spacer" />
      {item(
        "/account",
        t("nav.account"),
        <UserIcon className="sidenav__icon" />,
        current === "account"
      )}
      {/* Always in reach (Sigge 2026-10-07: "have a logout button
          somewhere"); a fresh page after, so nothing of this person stays. */}
      <button
        type="button"
        className="sidenav__item"
        onClick={() => {
          void portalApi
            .logout()
            .catch(() => undefined)
            .then(() => freshPage("/"));
        }}
      >
        <LogoutIcon className="sidenav__icon" />
        <span className="sidenav__label">{t("account.logout")}</span>
      </button>
    </nav>
  );
}

/**
 * "Tillbaka": one control, top-left above every inner page, that follows
 * the visitor's own path through the portal (`Navigation.back`): the
 * previous in-app page when there is one - with its scroll position put
 * back - else the page's natural parent, so a visitor who arrived from a
 * link outside never gets sent off the site. A real button (Enter / Space),
 * not on the store's home.
 */
export function BackButton() {
  const { t } = useI18n();
  const { back } = usePortal();
  return (
    <nav className="backbar" aria-label={t("nav.backbar")}>
      <button type="button" className="back" onClick={back}>
        <ArrowLeft className="back__icon" />
        {t("common.back")}
      </button>
    </nav>
  );
}

function SearchBox({ route }: { route: Route }) {
  const { t } = useI18n();
  const { replace, go } = usePortal();
  const onStore = route.name === "browse";
  const routeQ = route.name === "browse" ? route.q : "";
  const committed = searchable(routeQ);
  const [input, setInput] = useState(routeQ);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const hintId = useId();

  // Back / forward (or leaving the store) moved the query: follow it.
  useEffect(() => {
    setInput((prev) =>
      searchable(prev) === searchable(routeQ) ? prev : routeQ
    );
  }, [routeQ]);

  const commit = (next: string) => {
    if (onStore) replace(searchPath(next));
    else if (next) go(searchPath(next));
  };

  // Debounced commit of what is typed.
  useEffect(() => {
    const next = searchable(input);
    if (next === committed || (!onStore && !next)) return;
    const timer = window.setTimeout(() => commit(next), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
    // `commit` closes over the same values listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, committed, onStore]);

  const clear = () => {
    setInput("");
    if (onStore) replace("/");
    inputRef.current?.focus();
  };

  const short = input.trim() !== "" && !searchable(input);
  return (
    <form
      role="search"
      className="header-search"
      onSubmit={(e) => {
        e.preventDefault();
        const next = searchable(input);
        if (next !== committed) commit(next);
      }}
    >
      <label htmlFor={inputId} className="sr-only">
        {t("store.search.label")}
      </label>
      <SearchIcon className="header-search__icon" />
      <input
        ref={inputRef}
        id={inputId}
        type="search"
        className="header-search__input"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder={t("store.search.placeholder")}
        autoComplete="off"
        enterKeyHint="search"
        aria-describedby={short ? hintId : undefined}
      />
      {input && (
        <button
          type="button"
          className="header-search__clear"
          onClick={clear}
          aria-label={t("store.search.clear")}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
      {short && (
        <p id={hintId} className="header-search__hint" role="status">
          {t("store.search.short")}
        </p>
      )}
    </form>
  );
}

export function Footer() {
  const { context } = usePortal();
  const { t, lang } = useI18n();
  const s = context.settings;
  const logo = context.skin.theme?.logoUrl;
  const copyright = context.skin.identity?.copyright;
  const tagline = portalLine(context.appearance?.tagline, lang);
  const hasLinks = !!(s.termsUrl || s.privacyUrl || s.supportEmail);
  return (
    <footer className="footer">
      <div className="footer__inner wrap">
        <div className="footer__brand">
          {logo ? <img className="footer__logo" src={logo} alt="" /> : null}
          {/* With a logo the name is already drawn; keep it for readers. */}
          <p className={logo ? "footer__name sr-only" : "footer__name"}>
            {context.portal.name}
          </p>
          {tagline && <p className="footer__tagline">{tagline}</p>}
        </div>
        {hasLinks && (
          <nav className="footer__links" aria-label={t("footer.nav")}>
            <ul>
              {s.termsUrl && (
                <li>
                  <a href={s.termsUrl} target="_blank" rel="noreferrer">
                    {t("footer.terms")}
                  </a>
                </li>
              )}
              {s.privacyUrl && (
                <li>
                  <a href={s.privacyUrl} target="_blank" rel="noreferrer">
                    {t("footer.privacy")}
                  </a>
                </li>
              )}
              {s.supportEmail && (
                <li>
                  {t("footer.support", { email: "" })}
                  <a href={`mailto:${s.supportEmail}`}>{s.supportEmail}</a>
                </li>
              )}
            </ul>
          </nav>
        )}
      </div>
      {context.attributions?.includes("tmdb") && (
        <div
          className="footer__attribution wrap"
          data-testid="tmdb-attribution"
        >
          <p>
            {t("footer.tmdb")}{" "}
            <a
              href="https://www.themoviedb.org/"
              target="_blank"
              rel="noreferrer"
            >
              TMDB
            </a>
            .
          </p>
          <p>{t("footer.tmdbNotice")}</p>
        </div>
      )}
      {typeof copyright === "string" && copyright.trim() && (
        <p className="footer__copy wrap">{copyright}</p>
      )}
    </footer>
  );
}
